import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { STALE } from '@/lib/query-cache.js';
import type { JiraIssueLinkType, JiraIssuePickerResult } from '@shared/types.js';

export function useLinkTypes() {
  return useQuery({
    queryKey: ['meta', 'link-types'],
    queryFn: () => api.get<JiraIssueLinkType[]>('/api/meta/link-types').then((r) => r.data),
    staleTime: STALE.METADATA,
  });
}

export function useIssuePicker(query: string, excludeKey: string) {
  const q = query.trim();
  return useQuery({
    queryKey: ['issue-picker', q, excludeKey],
    queryFn: () => {
      const params = new URLSearchParams({ q, exclude: excludeKey });
      return api.get<JiraIssuePickerResult[]>(`/api/issues/picker?${params}`).then((r) => r.data);
    },
    enabled: q.length >= 2,
    staleTime: STALE.ACTIVE,
    placeholderData: (prev) => prev,
  });
}

/** Links change both ends, plus any list/board showing link indicators. */
function invalidateLinkViews(queryClient: QueryClient, issueKey: string, targetKey: string) {
  void queryClient.invalidateQueries({ queryKey: ['issue', issueKey] });
  void queryClient.invalidateQueries({ queryKey: ['issue', targetKey] });
  void queryClient.invalidateQueries({ queryKey: ['audit', issueKey] });
  void queryClient.invalidateQueries({ queryKey: ['issues'] });
  void queryClient.invalidateQueries({ queryKey: ['board'] });
}

interface CreateLinkInput {
  typeName: string;
  direction: 'inward' | 'outward';
  targetKey: string;
  relation: string;
}

export function useCreateIssueLink(issueKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLinkInput) => api.post(`/api/issues/${issueKey}/links`, input),
    onSuccess: (_data, { targetKey }) => invalidateLinkViews(queryClient, issueKey, targetKey),
  });
}

interface DeleteLinkInput {
  linkId: string;
  /** Other end of the link, used to refresh its view too */
  targetKey: string;
}

export function useDeleteIssueLink(issueKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId }: DeleteLinkInput) => api.delete(`/api/issues/${issueKey}/links/${linkId}`),
    onSuccess: (_data, { targetKey }) => invalidateLinkViews(queryClient, issueKey, targetKey),
  });
}
